import { useEffect, useRef, useState } from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import { triggerHaptic } from "@/lib/nativeHaptics";
import { trackEvent } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { invokeGenerator } from "@/lib/invokeGenerator";


const OCCASIONS = ["casual", "work", "date night", "formal", "holiday", "weekend"] as const;
const SEASONS = ["spring", "summer", "autumn", "winter"] as const;

const MAX_CHARS = 500;
const MIN_CHARS = 5;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string | null;
  guestSessionId: string | null;
  /** Called once outfit row is pre-inserted + edge fn invoked. */
  onSubmitted: (args: {
    outfitId: string;
    textDescription: string;
    occasion: string | null;
    season: string | null;
  }) => void;
  /**
   * PQ-3b: guest gate. Return false to block submission (paywall opens via
   * parent side effect). Default: always allowed.
   */
  canSubmit?: () => boolean;
}

export default function TextDescriptionSheet({
  open,
  onOpenChange,
  userId,
  guestSessionId,
  onSubmitted,
  canSubmit,
}: Props) {
  const [value, setValue] = useState("");
  const [occasion, setOccasion] = useState<string | null>(null);
  const [season, setSeason] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!open) {
      setValue("");
      setOccasion(null);
      setSeason(null);
      setSubmitting(false);
    }
  }, [open]);

  const trimmed = value.trim();
  const ctaEnabled = !submitting && trimmed.length >= MIN_CHARS;

  const handleSubmit = async () => {
    if (!ctaEnabled) return;

    // PQ-3b: guest paywall gate fires BEFORE outfit insert + edge fn.
    if (canSubmit && !canSubmit()) {
      onOpenChange(false);
      return;
    }

    void triggerHaptic("success");
    setSubmitting(true);

    const outfitId = crypto.randomUUID();
    const isGuest = !userId;

    const { error: insertError } = await supabase.from("outfits").insert({
      id: outfitId,
      user_id: userId ?? null,
      is_guest: isGuest,
      guest_session_id: isGuest ? guestSessionId : null,
      image_url: null,
      lookbook_status: "pending",
      analysis_status: "pending",
      input_method: "text",
      source_type: "text",
      text_description: trimmed,
      occasion,
    } as never);

    if (insertError) {
      console.error("[F37.3] text outfits insert failed", insertError);
      setSubmitting(false);
      toast.error(insertError.message ?? "Couldn't submit description");
      return;
    }

    trackEvent("text_outfit_submitted", {
      length: trimmed.length,
      has_occasion: !!occasion,
      has_season: !!season,
    });

    // Resolve gender so the orchestrator can prompt GPT/SerpApi/LB1 correctly.
    // Authed: profile.size_gender → profile.gender (normalise woman/man → women/men).
    // Guest: localStorage('guest_gender') (matches screenshot flow).
    // Anything else: leave undefined; server defaults to "women" for SerpApi.
    let gender: "men" | "women" | undefined;
    if (userId) {
      try {
        const { data: profile } = await supabase
          .from("profiles")
          .select("size_gender, gender")
          .eq("id", userId)
          .maybeSingle();
        const raw =
          (profile as { size_gender?: string | null; gender?: string | null } | null)
            ?.size_gender ??
          (profile as { gender?: string | null } | null)?.gender ??
          null;
        if (raw === "woman" || raw === "women") gender = "women";
        else if (raw === "man" || raw === "men") gender = "men";
      } catch (err) {
        console.warn("[F37.3] profile gender lookup failed", err);
      }
    } else if (typeof window !== "undefined") {
      const guestGender = window.localStorage.getItem("guest_gender");
      if (guestGender === "women" || guestGender === "men") {
        gender = guestGender;
      }
    }

    // Fire-and-forget the edge fn; the watcher polls outfits → items_ready.
    void invokeGenerator("analyse-outfit-text", {
      source: "text-sheet",
      body: {
        outfit_id: outfitId,
        text_description: trimmed,
        occasion,
        season,
        gender,
        user_id: userId ?? null,
        is_guest: isGuest,
        guest_session_id: isGuest ? guestSessionId : null,
      },
    })
      .then(async ({ data, error: invokeError, quotaExceeded }) => {
        if (quotaExceeded) return;
        if (invokeError || (data && (data as { ok?: boolean }).ok === false)) {
          console.error("[F37.3] analyse-outfit-text failed", { invokeError, data });
          toast.error("We couldn't process that description. Please try again.");
          await supabase
            .from("outfits")
            .update({ analysis_status: "failed", lookbook_status: "failed" } as never)
            .eq("id", outfitId);
        }
      });



    onSubmitted({ outfitId, textDescription: trimmed, occasion, season });
  };

  const inputBorder = "1px solid #E8E2D9";

  return (
    <DrawerPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay
          className="fixed inset-0 z-50"
          style={{ background: "rgba(44,40,32,0.4)" }}
        />
        <DrawerPrimitive.Content
          className="fixed inset-x-0 z-50 flex flex-col"
          style={{
            bottom: "calc(78px + var(--safe-bottom))",
            background: "#FFFCF8",
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            borderBottomLeftRadius: 20,
            borderBottomRightRadius: 20,
            marginLeft: 8,
            marginRight: 8,
            outline: "none",
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: 36,
              height: 4,
              borderRadius: 999,
              background: "#E0D8D0",
              margin: "8px auto",
            }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              padding: "0 16px 4px",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                className="l2f-type-section-header"
                style={{ color: "#C84B2F" }}
              >
                DESCRIBE A LOOK
              </div>
              <DrawerPrimitive.Title
                style={{
                  fontSize: 20,
                  fontStyle: "italic",
                  fontFamily:
                    "var(--font-serif), 'Instrument Serif', Georgia, serif",
                  color: "#2C2820",
                  lineHeight: 1.1,
                  marginTop: 2,
                  fontWeight: 400,
                }}
              >
                Describe the look you're after
              </DrawerPrimitive.Title>
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={() => onOpenChange(false)}
              style={{
                width: 44,
                height: 44,
                background: "transparent",
                border: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
                padding: 0,
                margin: "-6px -6px 0 0",
              }}
            >
              <span
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 999,
                  background: "#F1EFE8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i className="ti ti-x" style={{ fontSize: 14, color: "#4A4540" }} />
              </span>
            </button>
          </div>
          <DrawerPrimitive.Description className="sr-only">
            Describe an outfit in your own words.
          </DrawerPrimitive.Description>

          <div style={{ padding: "10px 16px 6px" }}>
            <div
              style={{
                width: "100%",
                background: "#FFFCF8",
                border: inputBorder,
                borderRadius: 12,
                padding: "10px 14px",
              }}
            >
              <textarea
                ref={textareaRef}
                value={value}
                onChange={(e) => setValue(e.target.value.slice(0, MAX_CHARS))}
                rows={4}
                placeholder="e.g. casual weekend brunch outfit with sneakers"
                style={{
                  width: "100%",
                  fontSize: 16,
                  color: "#2C2820",
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  resize: "none",
                  fontFamily: "inherit",
                  lineHeight: 1.45,
                }}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  fontSize: 10,
                  color: "#9B958E",
                  marginTop: 4,
                }}
              >
                {value.length}/{MAX_CHARS}
              </div>
            </div>
          </div>

          <ChipRow
            label="OCCASION"
            options={OCCASIONS}
            value={occasion}
            onChange={setOccasion}
          />
          <ChipRow
            label="SEASON"
            options={SEASONS}
            value={season}
            onChange={setSeason}
          />

          <div style={{ padding: "12px 16px 16px" }}>
            <button
              type="button"
              disabled={!ctaEnabled}
              onClick={() => void handleSubmit()}
              style={{
                width: "100%",
                padding: 12,
                minHeight: 44,
                borderRadius: 22,
                fontSize: 14,
                fontWeight: 500,
                border: "none",
                cursor: ctaEnabled ? "pointer" : "not-allowed",
                background: ctaEnabled ? "#C84B2F" : "#D3D1C7",
                color: ctaEnabled ? "#fff" : "#9B958E",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              {submitting ? (
                <>
                  <i
                    className="ti ti-loader"
                    style={{ fontSize: 14, animation: "spin 1s linear infinite" }}
                  />
                  Submitting...
                </>
              ) : (
                "Find this look"
              )}
            </button>
          </div>
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  );
}

function ChipRow({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: readonly string[];
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  return (
    <div style={{ padding: "8px 16px 0" }}>
      <div
        className="l2f-type-section-header"
        style={{
          color: "#9B958E",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {options.map((opt) => {
          const active = value === opt;
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(active ? null : opt)}
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                fontSize: 12,
                background: active ? "#C84B2F" : "#FAECE7",
                color: active ? "#fff" : "#4A4540",
                border: "none",
                cursor: "pointer",
                textTransform: "capitalize",
                fontWeight: 500,
              }}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}
